import React from 'react';
import styles from './ExerciseSetHistoryDialog.module.scss';
import BaseDialog from '../DialogWrappers/BaseDialog';
import type { ExerciseSetStatistic } from '../../types';
import { Tooltip, Paper } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import classNames from 'classnames';
import {
  calculateConcentricCompliance,
  calculateEccentricCompliance,
  calculateRepsCompliance,
} from '../../utils/statistics';

interface ExerciseSetHistoryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  exerciseSets: { set: ExerciseSetStatistic; date: string }[];
}

const RepDetailsTooltip: React.FC<{
  set: ExerciseSetStatistic;
  isConcentric: boolean;
}> = ({ set, isConcentric }) => {
  const { analyzedMetrics, metrics } = set;
  if (!analyzedMetrics || !metrics) return null;

  // NOTE: This is a danger point, old data may not have .concentric/eccentric.type...
  const metricType = isConcentric
    ? metrics.concentric.type
    : metrics.eccentric.type;

  const { min, max } = isConcentric ? metrics.concentric : metrics.eccentric;
  const targetRange =
    typeof min === 'number' && typeof max === 'number'
      ? metricType === 'Tempo'
        ? `${min.toFixed(1)}s - ${max.toFixed(1)}s`
        : `${min.toFixed(1)} m/s - ${max.toFixed(1)} m/s`
      : '-';

  const headerLabel = metricType === 'Tempo' ? 'TEMPO' : 'VELOCITY';

  return (
    <Paper className={styles.tooltipContent} elevation={0}>
      <table className={styles.tooltipTable}>
        <thead>
          <tr>
            <th>REP #</th>
            <th>TARGET {headerLabel}</th>
            <th>ACTUAL {headerLabel}</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(analyzedMetrics).map(([repIndex, metric], index) => {
            const actual = isConcentric
              ? metricType === 'Tempo'
                ? metric.upDuration
                : metric.upVelocity
              : metricType === 'Tempo'
                ? metric.downDuration
                : metric.downVelocity;
            const numericRepIndex = Number(repIndex);
            const displayRepNumber = Number.isNaN(numericRepIndex)
              ? index + 1
              : numericRepIndex || index + 1;
            return (
              <tr key={repIndex}>
                <td>{displayRepNumber}</td>
                <td>{targetRange}</td>
                <td>
                  {typeof actual === 'number'
                    ? `${Math.floor(actual * 10) / 10}${metricType === 'Tempo' ? 's' : ' m/s'}`
                    : '-'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Paper>
  );
};

const ExerciseSetHistoryDialog: React.FC<ExerciseSetHistoryDialogProps> = ({
  isOpen,
  onClose,
  exerciseSets,
}) => {
  const renderSetRow = (
    { set, date }: { set: ExerciseSetStatistic; date: string },
    index: number,
  ) => {
    const formattedDate = new Date(date).toLocaleDateString();
    const reps = set.performedReps;
    const weight = set.performedWeight;
    const repCompliance = calculateRepsCompliance(
      set.performedReps,
      set.targetReps,
    );
    const concentricAccuracy = calculateConcentricCompliance(set);
    const eccentricAccuracy = calculateEccentricCompliance(set);

    const hasHistory = exerciseSets.length > 0;

    return (
      <div
        className={classNames(styles.setRow, {
          [styles.clickable]: hasHistory,
        })}
        key={index}
      >
        <div className={styles.setColumn}>{formattedDate}</div>
        <div className={styles.setColumn}>{reps}</div>
        <div className={styles.setColumn}>{weight} lbs</div>
        <div className={styles.setColumn}>{repCompliance}</div>
        <div className={styles.setColumn}>
          {concentricAccuracy || '-'}
          {concentricAccuracy && (
            <Tooltip
              title={<RepDetailsTooltip set={set} isConcentric={true} />}
              placement="bottom-start"
              PopperProps={{
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      altAxis: true,
                      tether: false,
                      rootBoundary: 'viewport',
                    },
                  },
                ],
              }}
            >
              <InfoIcon className={styles.informationIcon} />
            </Tooltip>
          )}
        </div>
        <div className={styles.setColumn}>
          {eccentricAccuracy || '-'}
          {eccentricAccuracy && (
            <Tooltip
              title={<RepDetailsTooltip set={set} isConcentric={false} />}
              placement="bottom-start"
              PopperProps={{
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      altAxis: true,
                      tether: false,
                      rootBoundary: 'viewport',
                    },
                  },
                ],
              }}
            >
              <InfoIcon className={styles.informationIcon} />
            </Tooltip>
          )}
        </div>
      </div>
    );
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Exercise Set History"
      children={
        <div className={styles.content}>
          <div className={styles.tableHeader}>
            <div className={styles.setColumnHeader}>DATE</div>
            <div className={styles.setColumnHeader}>REPS</div>
            <div className={styles.setColumnHeader}>WEIGHT</div>
            <div className={styles.setColumnHeader}>REP COMPLIANCE</div>
            <div className={styles.setColumnHeader}>CONCENTRIC ACCURACY</div>
            <div className={styles.setColumnHeader}>ECCENTRIC ACCURACY</div>
          </div>
          <div className={styles.tableBody}>
            {exerciseSets.map((set, index) => renderSetRow(set, index))}
          </div>
        </div>
      }
      actions={<div />}
    />
  );
};

export default ExerciseSetHistoryDialog;
